import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import API, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

/**
 * Request Account modal.
 *
 * Fields:
 *   1. Commercial motion (radio group)
 *   2. Up to 3 free-text account names (1 minimum)
 *   3. Optional 200-char note
 *
 * Behaviour:
 *   - If the visitor isn't signed in we route them to /signup with a
 *     return-here intent before the form is shown — the spec assumes
 *     the requester profile (email, first_name, etc.) is on record.
 *   - On success, the modal swaps to a confirmation state instead of
 *     closing, so the requester sees the spec's confirmation copy.
 *   - Submission posts ONCE — backend creates one row per requested
 *     account in `account_requests`.
 *
 * Default motion comes from the user's `intake.role_family` if set;
 * otherwise the field is left blank and the user has to pick one.
 */
const MOTION_OPTIONS = [
  { value: "acquisition",  label: "Win new business",                 helper: "Hunting for new logos / new wallets." },
  { value: "retention",    label: "Grow existing business",           helper: "Expand a current account or renewal." },
  { value: "solutioning",  label: "Build solutioning & commercials",  helper: "Shape the offer + commercial case." },
  { value: "delivery",     label: "Deliver existing business",        helper: "Protect run-rate / programme delivery." },
];

const HELPER_COPY =
  "Tell us which FS accounts you want Kalib to cover. Requests help us prioritise new accounts, but they do not guarantee activation or timing.";
const SUCCESS_COPY =
  "Got it. If any of these accounts become active for your profile, we'll email you and show them in your account list.";

export default function RequestAccountModal({ open, onClose }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [motion, setMotion] = useState("");
  const [accountNames, setAccountNames] = useState([""]);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Reset state every time the modal re-opens. Default the motion to the
  // user's intake role_family if it maps to one of our four canonical
  // motions — saves the visitor a click for the most common case.
  useEffect(() => {
    if (!open) return;
    setSubmitted(false);
    setAccountNames([""]);
    setNote("");
    const def = (user?.intake?.role_family || "").toLowerCase();
    const valid = MOTION_OPTIONS.some((o) => o.value === def);
    setMotion(valid ? def : "");
  }, [open, user]);

  if (!open) return null;

  // Anonymous visitors: redirect to signup before they see the form.
  // This keeps the spec invariant ("requester email/name from session")
  // honest without requiring a guest-request branch on the backend.
  if (!user) {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/60 px-4"
        role="dialog"
        aria-modal="true"
        data-testid="request-account-modal"
      >
        <div className="bg-white border border-zinc-200 w-full max-w-md p-6">
          <h3 className="font-heading text-[18px] font-semibold text-zinc-950 mb-3">
            Sign up to request an account
          </h3>
          <p className="text-[14px] text-zinc-700 mb-5">
            Requests are scoped to your profile so we can route the activation
            email to you and shape the lens correctly. It takes a minute.
          </p>
          <div className="flex justify-end gap-2">
            <Button
              variant="ghost"
              onClick={onClose}
              data-testid="request-account-anon-cancel"
            >
              Not now
            </Button>
            <Button
              className="bg-amber-700 text-white hover:bg-amber-800 rounded-none"
              onClick={() => {
                onClose?.();
                navigate("/signup");
              }}
              data-testid="request-account-anon-signup"
            >
              Sign up
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const trimmedNames = accountNames.map((n) => n.trim()).filter(Boolean);
  const canSubmit =
    !!motion && trimmedNames.length >= 1 && trimmedNames.length <= 3 && note.length <= 200;

  const updateName = (idx, val) => {
    setAccountNames((prev) => prev.map((n, i) => (i === idx ? val : n)));
  };

  const addName = () => {
    if (accountNames.length >= 3) return;
    setAccountNames((prev) => [...prev, ""]);
  };

  const removeName = (idx) => {
    setAccountNames((prev) => (prev.length === 1 ? prev : prev.filter((_, i) => i !== idx)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      await API.post("/account-requests", {
        role_family_requested: motion,
        account_names: trimmedNames,
        request_note: note || null,
      });
      setSubmitted(true);
    } catch (err) {
      toast.error(formatApiError(err) || "Couldn't submit request — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-950/60 px-4 py-6"
      role="dialog"
      aria-modal="true"
      data-testid="request-account-modal"
    >
      <div className="bg-white border border-zinc-200 w-full max-w-xl max-h-[90vh] overflow-y-auto">
        <div className="border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
          <h3
            className="font-heading text-[20px] font-semibold text-zinc-950"
            data-testid="request-account-modal-title"
          >
            Request an account
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-500 hover:text-zinc-900 text-2xl leading-none px-2"
            aria-label="Close"
            data-testid="request-account-modal-close"
          >
            ×
          </button>
        </div>

        {submitted ? (
          <div className="px-6 py-8" data-testid="request-account-success">
            <p className="font-heading text-[16px] font-semibold text-zinc-950 mb-2">
              Request received
            </p>
            <p className="text-[14px] text-zinc-700 leading-relaxed">{SUCCESS_COPY}</p>
            <div className="mt-6 flex justify-end">
              <Button
                className="bg-amber-700 text-white hover:bg-amber-800 rounded-none"
                onClick={onClose}
                data-testid="request-account-success-close"
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-5 space-y-6 text-sm">
              <p className="text-[13px] text-zinc-600 leading-relaxed">{HELPER_COPY}</p>

              {/* 1. Commercial motion */}
              <fieldset>
                <legend className="block text-[11px] font-semibold uppercase tracking-widest text-zinc-700 mb-2">
                  Commercial motion
                </legend>
                <div className="space-y-2" role="radiogroup">
                  {MOTION_OPTIONS.map((opt) => (
                    <label
                      key={opt.value}
                      className={`flex items-start gap-3 px-3 py-2.5 border cursor-pointer transition-colors ${
                        motion === opt.value
                          ? "border-amber-700 bg-amber-50"
                          : "border-zinc-200 hover:border-zinc-300"
                      }`}
                      data-testid={`request-account-motion-${opt.value}`}
                    >
                      <input
                        type="radio"
                        name="request-account-motion"
                        value={opt.value}
                        checked={motion === opt.value}
                        onChange={() => setMotion(opt.value)}
                        className="mt-0.5 accent-amber-700"
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block text-[14px] font-medium text-zinc-950">
                          {opt.label}
                        </span>
                        <span className="block text-[12px] text-zinc-500 mt-0.5">
                          {opt.helper}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {/* 2. Account names — up to 3 */}
              <fieldset>
                <legend className="block text-[11px] font-semibold uppercase tracking-widest text-zinc-700 mb-2">
                  Accounts you want covered
                </legend>
                <div className="space-y-2">
                  {accountNames.map((n, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <input
                        type="text"
                        value={n}
                        onChange={(e) => updateName(idx, e.target.value)}
                        placeholder={idx === 0 ? "e.g. BNP Paribas" : "Optional — another account"}
                        maxLength={80}
                        className="flex-1 px-3 py-2 border border-zinc-200 bg-white text-zinc-900 focus:outline-none focus:border-amber-700 text-[14px]"
                        data-testid={`request-account-name-${idx}`}
                      />
                      {accountNames.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeName(idx)}
                          className="text-zinc-500 hover:text-zinc-900 text-xl leading-none px-2"
                          aria-label="Remove this account"
                          data-testid={`request-account-name-${idx}-remove`}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                  {accountNames.length < 3 && (
                    <button
                      type="button"
                      onClick={addName}
                      className="text-[13px] font-medium text-amber-700 hover:text-amber-800 transition-colors"
                      data-testid="request-account-name-add"
                    >
                      + Add another account
                    </button>
                  )}
                </div>
                <p className="mt-1.5 text-[11px] text-zinc-500">
                  Up to 3 accounts per request.
                </p>
              </fieldset>

              {/* 3. Optional note */}
              <label className="block">
                <span className="block text-[11px] font-semibold uppercase tracking-widest text-zinc-700 mb-2">
                  Note (optional)
                </span>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value.slice(0, 200))}
                  placeholder="A line on why this account matters to you, if it helps."
                  rows={3}
                  className="block w-full px-3 py-2 border border-zinc-200 bg-white text-zinc-900 focus:outline-none focus:border-amber-700 text-[14px] resize-none"
                  data-testid="request-account-note"
                />
                <p className="mt-1.5 text-[11px] text-zinc-500 text-right">
                  {note.length} / 200
                </p>
              </label>
            </div>

            <div className="border-t border-zinc-200 px-6 py-4 flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={onClose}
                disabled={submitting}
                data-testid="request-account-cancel"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!canSubmit || submitting}
                className="bg-amber-700 text-white hover:bg-amber-800 rounded-none disabled:opacity-50"
                data-testid="request-account-submit"
              >
                {submitting ? "Sending…" : "Send request"}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
